import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
    Building2,
    Search,
    Plus,
    ChevronRight,
    MapPin,
    Activity,
    Mail,
    Phone,
    FileText,
    RefreshCw,
    Sparkles,
    Database
} from 'lucide-react';
import SuperAdminLayout from '../../components/SuperAdmin/Layout/SuperAdminLayout';
import tenantService from '../../services/tenantService';

export default function Organizations() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [organizations, setOrganizations] = useState([]);
    const [selectedOrg, setSelectedOrg] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchOrganizations();
    }, []);

    const fetchOrganizations = async () => {
        setLoading(true);
        try {
            const data = await tenantService.superAdminGetAllOrganizations();
            setOrganizations(data);
            if (data.length > 0 && !selectedOrg) {
                setSelectedOrg(data[0]);
            }
        } catch (error) {
            toast.error("Failed to fetch organizations");
        } finally {
            setLoading(false);
        }
    };

    const filteredOrganizations = organizations.filter(org =>
        org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        org.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleBranchClick = (branch) => {
        navigate(`/platform/branch/${branch.id}`, {
            state: {
                branch,
                organization: selectedOrg
            }
        });
    };

    return (
        <SuperAdminLayout>
            <div className="flex h-full bg-gradient-to-br from-slate-50 via-white to-[#FFF4E6]/30">
                {/* LEFT PANEL - Organizations List */}
                <div className="w-[400px] border-r border-slate-200 bg-white flex flex-col">
                    {/* Search Header */}
                    <div className="p-6 border-b border-slate-100 bg-gradient-to-br from-white to-slate-50">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <h2 className="text-lg font-bold text-slate-900">Organizations</h2>
                                <p className="text-xs text-slate-500 mt-0.5">
                                    {organizations.length} total
                                </p>
                            </div>
                            <button
                                onClick={() => navigate('/platform/tenant-setup')}
                                className="p-2 bg-[#232F3E] hover:bg-[#37475A] text-white rounded-lg transition-colors shadow-sm"
                                title="Add New Organization"
                            >
                                <Plus size={18} />
                            </button>
                        </div>

                        {/* Search Input */}
                        <div className="relative">
                            <input
                                type="text"
                                placeholder="Search organizations..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804] outline-none transition-all"
                            />
                            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        </div>
                    </div>

                    {/* Organizations List */}
                    <div className="flex-1 overflow-y-auto custom-scrollbar">
                        {loading ? (
                            <div className="flex items-center justify-center h-32">
                                <RefreshCw className="animate-spin text-[#f08804]" size={24} />
                            </div>
                        ) : filteredOrganizations.length === 0 ? (
                            <div className="p-8 text-center text-slate-400">
                                <Building2 size={48} className="mx-auto mb-3 text-slate-300" />
                                <p className="text-sm font-medium">No organizations found</p>
                            </div>
                        ) : (
                            <div className="p-3 space-y-2">
                                {filteredOrganizations.map((org) => (
                                    <button
                                        key={org.id}
                                        onClick={() => setSelectedOrg(org)}
                                        className={`w-full text-left p-4 rounded-xl transition-all group ${
                                            selectedOrg?.id === org.id
                                                ? 'bg-gradient-to-br from-[#f08804] to-[#e88b00] text-white shadow-lg shadow-[#FFD89E]'
                                                : 'bg-slate-50 hover:bg-slate-100 text-slate-900'
                                        }`}
                                    >
                                        <div className="flex items-start justify-between mb-2">
                                            <div className="flex-1 min-w-0">
                                                <h3 className={`font-bold text-sm truncate mb-1 ${
                                                    selectedOrg?.id === org.id ? 'text-white' : 'text-slate-900'
                                                }`}>
                                                    {org.name}
                                                </h3>
                                                <p className={`text-xs truncate ${
                                                    selectedOrg?.id === org.id ? 'text-[#FFD89E]' : 'text-slate-500'
                                                }`}>
                                                    {org.email || 'No email'}
                                                </p>
                                            </div>
                                            <ChevronRight
                                                size={16}
                                                className={`flex-shrink-0 ml-2 transition-transform ${
                                                    selectedOrg?.id === org.id
                                                        ? 'text-white translate-x-1'
                                                        : 'text-slate-400 group-hover:translate-x-1'
                                                }`}
                                            />
                                        </div>

                                        <div className="flex items-center justify-between">
                                            <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium ${
                                                selectedOrg?.id === org.id
                                                    ? 'bg-white/20 text-white'
                                                    : 'bg-slate-200 text-slate-600'
                                            }`}>
                                                <MapPin size={12} />
                                                {org.branches?.length || 0} branches
                                            </span>

                                            {org.gstNumber && (
                                                <span className={`text-[10px] font-mono ${
                                                    selectedOrg?.id === org.id ? 'text-[#FFD89E]' : 'text-slate-400'
                                                }`}>
                                                    GST: {org.gstNumber.slice(-4)}
                                                </span>
                                            )}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* RIGHT PANEL - Branch Details */}
                <div className="flex-1 flex flex-col overflow-hidden">
                    {selectedOrg ? (
                        <>
                            {/* Organization Header */}
                            <div className="px-8 py-6 border-b border-slate-200 bg-gradient-to-r from-white via-[#FFF4E6]/30 to-purple-50/30">
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex items-center gap-4">
                                        <div className="w-14 h-14 bg-gradient-to-br from-[#f08804] to-[#e88b00] rounded-2xl flex items-center justify-center text-white shadow-lg shadow-[#FFD89E]">
                                            <Building2 size={28} />
                                        </div>
                                        <div>
                                            <h1 className="text-2xl font-bold text-slate-900 mb-1">
                                                {selectedOrg.name}
                                            </h1>
                                            <div className="flex items-center gap-4 text-sm text-slate-500">
                                                {selectedOrg.email && (
                                                    <span className="flex items-center gap-1.5">
                                                        <Mail size={14} />
                                                        {selectedOrg.email}
                                                    </span>
                                                )}
                                                {selectedOrg.contact && (
                                                    <span className="flex items-center gap-1.5">
                                                        <Phone size={14} />
                                                        {selectedOrg.contact}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <button
                                        onClick={() => navigate('/platform/tenant-setup', {
                                            state: { organization: selectedOrg }
                                        })}
                                        className="px-4 py-2 bg-white border border-slate-200 hover:border-[#FFD89E] hover:bg-[#FFF4E6] text-slate-700 rounded-lg text-sm font-medium flex items-center gap-2 transition-all shadow-sm"
                                    >
                                        <Plus size={16} />
                                        Add Branch
                                    </button>
                                </div>

                                {/* Organization Metadata */}
                                <div className="flex items-center gap-6 pt-4 border-t border-slate-100">
                                    <div className="flex items-center gap-2">
                                        <span className="px-2 py-1 bg-[#FFE8C8] text-[#C45500] rounded text-xs font-semibold uppercase">
                                            {selectedOrg.businessType || 'QSR'}
                                        </span>
                                    </div>
                                    {selectedOrg.gstNumber && (
                                        <div className="flex items-center gap-2 text-xs">
                                            <FileText size={14} className="text-amber-500" />
                                            <span className="font-mono text-slate-600">
                                                GST: {selectedOrg.gstNumber}
                                            </span>
                                        </div>
                                    )}
                                    <div className="flex items-center gap-2 text-xs">
                                        <Activity size={14} className="text-slate-400" />
                                        <span className="text-slate-500">
                                            ID: <code className="font-mono text-slate-700">{selectedOrg.id}</code>
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Branches Grid */}
                            <div className="flex-1 overflow-y-auto p-8">
                                {selectedOrg.branches && selectedOrg.branches.length > 0 ? (
                                    <>
                                        <div className="flex items-center justify-between mb-6">
                                            <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                                                <Sparkles size={20} className="text-[#f08804]" />
                                                Branch Locations
                                            </h2>
                                            <span className="text-sm text-slate-500">
                                                {selectedOrg.branches.length} {selectedOrg.branches.length === 1 ? 'branch' : 'branches'}
                                            </span>
                                        </div>

                                        <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-6">
                                            {selectedOrg.branches.map((branch) => (
                                                <button
                                                    key={branch.id}
                                                    onClick={() => handleBranchClick(branch)}
                                                    className="group relative p-6 bg-white hover:bg-gradient-to-br hover:from-white hover:to-[#FFF4E6]/50 border border-slate-200 hover:border-[#FFD89E] rounded-2xl transition-all hover:shadow-xl hover:shadow-indigo-100/50 text-left min-h-[240px] flex flex-col"
                                                >
                                                    {/* Branch Header */}
                                                    <div className="flex items-start justify-between mb-4">
                                                        <div className="flex-1 min-w-0 pr-2">

                                                            {/* ── Branch name + Active/Inactive badge ── */}
                                                            <div className="flex items-center gap-2 flex-wrap mb-1.5">
                                                                <h3 className="font-bold text-slate-900 text-lg group-hover:text-[#C45500] transition-colors leading-tight">
                                                                    {branch.name}
                                                                </h3>
                                                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold flex-shrink-0 ${
                                                                    branch.active
                                                                        ? 'bg-green-100 text-green-700'
                                                                        : 'bg-red-100 text-red-600'
                                                                }`}>
                                                                    <span className={`w-1.5 h-1.5 rounded-full ${
                                                                        branch.active ? 'bg-green-500 animate-pulse' : 'bg-red-500'
                                                                    }`} />
                                                                    {branch.active ? 'Active' : 'Inactive'}
                                                                </span>
                                                            </div>

                                                            {branch.email && (
                                                                <p className="text-sm text-slate-500 flex items-center gap-1.5 mb-1">
                                                                    <Mail size={12} className="flex-shrink-0" />
                                                                    <span className="truncate">{branch.email}</span>
                                                                </p>
                                                            )}
                                                            {branch.contact && (
                                                                <p className="text-sm text-slate-500 flex items-center gap-1.5">
                                                                    <Phone size={12} className="flex-shrink-0" />
                                                                    {branch.contact}
                                                                </p>
                                                            )}
                                                        </div>
                                                        <ChevronRight
                                                            size={20}
                                                            className="text-slate-400 group-hover:text-[#C45500] group-hover:translate-x-1 transition-all flex-shrink-0"
                                                        />
                                                    </div>

                                                    {/* Location Address */}
                                                    {branch.addresses?.[0] && (
                                                        <div className="mb-4 pb-4 border-b border-slate-100">
                                                            <div className="flex items-start gap-2 text-sm text-slate-600">
                                                                <MapPin size={14} className="text-slate-400 flex-shrink-0 mt-0.5" />
                                                                <div className="flex-1">
                                                                    <p className="leading-relaxed">
                                                                        {branch.addresses[0].street && (
                                                                            <span className="block">{branch.addresses[0].street}</span>
                                                                        )}
                                                                        <span>
                                                                            {branch.addresses[0].city}
                                                                            {branch.addresses[0].state && `, ${branch.addresses[0].state}`}
                                                                            {branch.addresses[0].pincode && ` - ${branch.addresses[0].pincode}`}
                                                                        </span>
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Spacer to push footer to bottom */}
                                                    <div className="flex-1"></div>

                                                    {/* Branch Footer - Database & ID */}
                                                    <div className="space-y-2.5 mt-auto">
                                                        {/* Database Badge */}
                                                        {branch.dbName ? (
                                                            <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
                                                                <Database size={14} className="text-green-600 flex-shrink-0" />
                                                                <span className="text-sm font-medium text-green-700 truncate">
                                                                    {branch.dbName}
                                                                </span>
                                                                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse ml-auto flex-shrink-0" />
                                                            </div>
                                                        ) : (
                                                            <div className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg">
                                                                <span className="text-sm text-slate-400 italic">No database configured</span>
                                                            </div>
                                                        )}

                                                        {/* Branch ID */}
                                                        <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg border border-slate-200">
                                                            <Activity size={12} className="text-slate-400 flex-shrink-0" />
                                                            <span className="text-xs text-slate-500 font-medium">Branch ID:</span>
                                                            <code className="text-xs font-mono text-slate-700 flex-1 truncate" title={branch.id}>
                                                                {branch.id}
                                                            </code>
                                                        </div>
                                                    </div>

                                                    {/* Hover Gradient Border */}
                                                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-[#f08804]/0 to-purple-500/0 group-hover:from-[#f08804]/5 group-hover:to-purple-500/5 pointer-events-none transition-all" />
                                                </button>
                                            ))}
                                        </div>
                                    </>
                                ) : (
                                    <div className="flex items-center justify-center h-full">
                                        <div className="text-center max-w-md">
                                            <div className="w-20 h-20 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                                <MapPin size={40} className="text-slate-300" />
                                            </div>
                                            <h3 className="text-lg font-bold text-slate-900 mb-2">
                                                No Branches Yet
                                            </h3>
                                            <p className="text-sm text-slate-500 mb-6">
                                                This organization doesn't have any branch locations configured yet.
                                            </p>
                                            <button
                                                onClick={() => navigate('/platform/tenant-setup', {
                                                    state: { organization: selectedOrg }
                                                })}
                                                className="px-6 py-3 bg-[#232F3E] hover:bg-[#37475A] text-white rounded-xl font-medium flex items-center gap-2 mx-auto transition-all shadow-lg shadow-[#FFD89E]"
                                            >
                                                <Plus size={18} />
                                                Add First Branch
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        <div className="flex items-center justify-center h-full">
                            <div className="text-center">
                                <Building2 size={64} className="text-slate-200 mx-auto mb-4" />
                                <p className="text-slate-400 font-medium">
                                    Select an organization to view details
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <style jsx>{`
                .custom-scrollbar::-webkit-scrollbar {
                    width: 6px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: #f1f5f9;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: #cbd5e1;
                    border-radius: 3px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                    background: #94a3b8;
                }
            `}</style>
        </SuperAdminLayout>
    );
}